import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useDeleteManager } from '@/hooks/useManagers';
import { AddManagerModal } from '@/components/shared/AddManagerModal';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Manager } from '@jobos/shared/types/manager';
import { formatManagerName } from '@jobos/shared';

interface ManagersSectionProps {
  clientId: string;
  managers: Manager[];
}

export function ManagersSection({ clientId, managers }: ManagersSectionProps) {
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [editingManager, setEditingManager] = useState<Manager | null>(null);

  // Manager mutations
  const { mutateAsync: deleteManager } = useDeleteManager();

  const handleAddManager = () => {
    setEditingManager(null);
    setShowManagerDialog(true);
  };

  const handleEditManager = (manager: Manager) => {
    setEditingManager(manager);
    setShowManagerDialog(true);
  };

  const handleCloseModal = () => {
    setShowManagerDialog(false);
    setEditingManager(null);
  };

  const handleDeleteManager = async (manager: Manager) => {
    const displayName = formatManagerName(manager.name, manager.firstName, manager.lastName);
    if (confirm(`Are you sure you want to delete ${displayName}?`)) {
      try {
        await deleteManager(manager.id);
      } catch (error) {
        console.error('Failed to delete manager:', error);
      }
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Managers ({managers.length})
            </h2>
            <Button size="sm" onClick={handleAddManager}>
              Add Manager
            </Button>
          </div>

          {managers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managers.map((manager) => (
                  <TableRow key={manager.id}>
                    <TableCell className="font-medium">{formatManagerName(manager.name, manager.firstName, manager.lastName)}</TableCell>
                    <TableCell>{manager.email || '-'}</TableCell>
                    <TableCell>{manager.phone || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditManager(manager)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteManager(manager)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No managers found for this client</p>
              <Button className="mt-4" size="sm" onClick={handleAddManager}>
                Add First Manager
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Manager Dialog */}
      <AddManagerModal
        open={showManagerDialog}
        onOpenChange={handleCloseModal}
        clientId={clientId}
        editingManager={editingManager}
        onManagerCreated={handleCloseModal}
        onManagerUpdated={handleCloseModal}
      />
    </>
  );
}
